import { useMemo, useCallback, useState, useEffect } from 'react'

import {
  BrandNode,
  CarModelNode,
  CarSubModelNode,
  useAllBrandsQuery,
  useBrandQuery,
  useCarModelQuery,
} from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { useApp } from '@/hooks'
import { getFetchMoreStatus } from '@/utils'

interface IUseCarModelSelect {
  loading: boolean
  initialBrand: Maybe<Partial<BrandNode>>
  initialModel: Maybe<Partial<CarModelNode>>
  initialSubModel: Maybe<Partial<CarSubModelNode>>
}

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useCarModelSelect = ({
  loading,
  initialBrand,
  initialModel,
  initialSubModel,
}: IUseCarModelSelect) => {
  const { isRTL } = useApp()
  const [brands, setBrands] = useState<Partial<BrandNode>[]>([])
  const [brandSearch, setBrandSearch] = useState<string>('')
  const [brandsPageInfo, setBrandsPageInfo] =
    useState<IPageInfo>(initialPageInfo)

  const [models, setModels] = useState<Partial<CarModelNode>[]>([])
  const [modelsSearch, setModelsSearch] = useState<string>('')
  const [modelsPageInfo, setModelsPageInfo] =
    useState<IPageInfo>(initialPageInfo)

  const [subModels, setSubModels] = useState<Partial<CarSubModelNode>[]>([])
  const [subModelsSearch, setSubModelsSearch] = useState<string>('')
  const [subModelsPageInfo, setSubModelsPageInfo] =
    useState<IPageInfo>(initialPageInfo)

  const [brandId, setBrandId] = useState<string | undefined | null>()
  const [modelId, setModelId] = useState<string | undefined | null>()
  const [subModelId, setSubModelId] = useState<string | undefined | null>()

  useEffect(() => {
    setBrandId(initialBrand?.id || null)
  }, [initialBrand?.id])

  useEffect(() => {
    setModelId(initialModel?.id || null)
  }, [initialModel?.id])

  useEffect(() => {
    setSubModelId(initialSubModel?.id || null)
  }, [initialSubModel?.id])

  const { loading: loadingBrands, networkStatus: brandsNetworkStatus } =
    useAllBrandsQuery({
      fetchPolicy: 'network-only',
      skip: loading,
      variables: {
        offset: brandsPageInfo?.offset || 0,
        first: pageSize,
        nameIcontains: brandSearch,
      },
      onCompleted: res => {
        if (res.allBrands?.edges) {
          const _brands: Partial<BrandNode>[] = []
          res.allBrands.edges.forEach(e => {
            if (e?.node) _brands.push(e.node)
          })
          setBrands(prev => [...prev, ..._brands])
          setBrandsPageInfo(prev => ({
            ...prev,
            hasNextPage: res.allBrands?.pageInfo.hasNextPage,
          }))
        }
      },
    })

  const { loading: loadingModels, networkStatus: modelsNetworkStatus } =
    useBrandQuery({
      fetchPolicy: 'network-only',
      skip: !brandId,
      variables: {
        id: brandId || '',
        offset: modelsPageInfo?.offset || 0,
        first: pageSize,
        nameIcontains: modelsSearch,
      },
      onCompleted: res => {
        if (res.brand?.carmodelSet?.edges) {
          const _models: Partial<CarModelNode>[] = []
          res.brand.carmodelSet.edges.forEach(e => {
            if (e?.node) _models.push(e.node)
          })
          setModels(prev => [...prev, ..._models])
          setModelsPageInfo(prev => ({
            ...prev,
            hasNextPage: res.brand?.carmodelSet?.pageInfo.hasNextPage,
          }))
        }
      },
    })

  const { loading: loadingSubModels, networkStatus: subModelsNetworkStatus } =
    useCarModelQuery({
      fetchPolicy: 'network-only',
      skip: !modelId,
      variables: {
        id: modelId || '',
        offset: subModelsPageInfo?.offset || 0,
        first: pageSize,
        nameIcontains: subModelsSearch,
      },
      onCompleted: res => {
        if (res.carModel?.carsubmodelSet?.edges) {
          const _subModels: Partial<CarSubModelNode>[] = []
          res.carModel.carsubmodelSet.edges.forEach(e => {
            if (e?.node) _subModels.push(e.node)
          })
          setSubModels(prev => [...prev, ..._subModels])
          setSubModelsPageInfo(prev => ({
            ...prev,
            hasNextPage: res.carModel?.carsubmodelSet?.pageInfo.hasNextPage,
          }))
        }
      },
    })

  useEffect(() => {
    setModels([])
    setModelsPageInfo(initialPageInfo)
  }, [brandId])

  useEffect(() => {
    setSubModels([])
    setSubModelsPageInfo(initialPageInfo)
  }, [modelId])

  const brandOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = brands.map(s => {
      if (!exists && initialBrand?.id === s.id) {
        exists = true
      }
      return {
        label: s.name || '',
        value: s.id || '',
      }
    })

    if (!exists && initialBrand?.name && initialBrand?.id && !brandSearch) {
      opts.unshift({
        label: initialBrand.name || '',
        value: initialBrand.id || '',
      })
    }
    return opts
  }, [brandSearch, brands, initialBrand?.id, initialBrand?.name])

  const modelOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const shouldHaveInitial = brandId === initialBrand?.id && !modelsSearch
    const opts = models.map(s => {
      if (!exists && initialModel?.id === s.id) {
        exists = true
      }
      return {
        label: (isRTL ? s.localName || s.name : s.name || s.localName) || '',
        value: s.id || '',
      }
    })
    if (!exists && shouldHaveInitial && initialModel?.id) {
      opts.unshift({
        label:
          (isRTL
            ? initialModel?.localName || initialModel?.name
            : initialModel?.name || initialModel?.localName) || '',
        value: initialModel.id || '',
      })
    }
    return opts
  }, [
    brandId,
    initialBrand?.id,
    initialModel?.id,
    initialModel?.localName,
    initialModel?.name,
    isRTL,
    models,
    modelsSearch,
  ])

  const subModelOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const shouldHaveInitial =
      brandId === initialBrand?.id &&
      modelId === initialModel?.id &&
      !subModelsSearch
    const opts = subModels.map(s => {
      if (!exists && initialSubModel?.id === s.id) {
        exists = true
      }
      return {
        label: (isRTL ? s.localName || s.name : s.name || s.localName) || '',
        value: s.id || '',
      }
    })
    if (!exists && initialSubModel?.id && shouldHaveInitial) {
      opts.unshift({
        label:
          initialSubModel.id +
            '-' +
            (isRTL
              ? initialSubModel?.localName || initialSubModel?.name
              : initialSubModel?.name || initialSubModel?.localName) || '',
        value: initialSubModel.id || '',
      })
    }
    return opts
  }, [
    brandId,
    initialBrand?.id,
    initialModel?.id,
    initialSubModel?.id,
    initialSubModel?.localName,
    initialSubModel?.name,
    isRTL,
    modelId,
    subModels,
    subModelsSearch,
  ])

  useEffect(() => {
    setBrandsPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus: brandsNetworkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [brandsNetworkStatus])

  useEffect(() => {
    setModelsPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus: modelsNetworkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [modelsNetworkStatus])

  useEffect(() => {
    setSubModelsPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus: subModelsNetworkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [subModelsNetworkStatus])

  const onFetchMore = useCallback(
    (field: 'brands' | 'models' | 'submodels') => {
      if (field === 'brands') {
        setBrandsPageInfo(prev => ({ ...prev, offset: brands.length }))
      } else if (field === 'models') {
        setModelsPageInfo(prev => ({ ...prev, offset: models.length }))
      } else if (field === 'submodels') {
        setSubModelsPageInfo(prev => ({ ...prev, offset: subModels.length }))
      }
    },
    [brands.length, models.length, subModels.length],
  )

  const onSearch = useCallback(
    (field: 'brands' | 'models' | 'submodels', text: string) => {
      if (field === 'brands') {
        if (brandSearch !== text) {
          setBrands([])
        }
        setBrandSearch(text)
        setBrandsPageInfo(initialPageInfo)
      } else if (field === 'models') {
        if (modelsSearch !== text) {
          setModels([])
        }
        setModelsSearch(text)
        setModelsPageInfo(initialPageInfo)
      } else if (field === 'submodels') {
        if (subModelsSearch !== text) {
          setSubModels([])
        }
        setSubModelsSearch(text)
        setSubModelsPageInfo(initialPageInfo)
      }
    },
    [brandSearch, modelsSearch, subModelsSearch],
  )

  const onFieldChange = useCallback(
    (n: 'carBrand' | 'carModel' | 'carSubModel', v?: any) => {
      if (n === 'carBrand') {
        setBrandId(v)
        setModelId(null)
        setSubModelId(null)
      } else if (n === 'carModel') {
        setModelId(v)
        setSubModelId(null)
      } else if (n === 'carSubModel') {
        setSubModelId(v)
      }
    },
    [],
  )

  return {
    brandId,
    modelId,
    subModelId,
    brandOptions,
    modelOptions,
    subModelOptions,
    loadingBrands,
    loadingModels,
    loadingSubModels,
    brandsPageInfo,
    modelsPageInfo,
    subModelsPageInfo,
    onFetchMore,
    onFieldChange,
    onSearch,
  }
}
